// ts-strict-ignore
import PropTypes from 'prop-types';
import range from 'lodash/range';
import { UncontrolledTooltip as Tooltip } from 'reactstrap';
import styled from 'styled-components';
import {
  Box,
  Button,
  ButtonVariant,
  Checkbox,
  ErrorBoundary,
  Icon,
  IconName,
} from '@usealloy/component-library';

import EvaluationCompleteSVG from '~/evaluations/EvaluationCompleteSVG';
import MultiStepModal from '~/evaluations/form/MultiStepModal';
import RunEvaluationForm, {
  RunEvaluationFormDefaultProps,
  RunEvaluationFormPropTypes,
} from '~/evaluations/RunEvaluationForm';
import { ajaxFetch } from '~/helpers/api';
import { formattedFormData } from '~/helpers/formData';
import { routes } from '~/helpers/routes';
import WithDashboardHooksHOC from '~/helpers/WithDashboardHooksHOC';
import { tooltipZIndexModifiers } from '~/constants/reactstrap';
import log from '~/helpers/logging';
import type { Entity, Evaluation } from '~/entities/types';

import { evaluationHasMetaFormData, getRerunFormData } from './helpers';

const CheckboxWrapper = styled.div`
  label {
    margin-bottom: 0;
  }
`;

type RerunEntity = Entity & {
  evaluations_data_url?: string;
};

export type RerunEvaluationFormProps = {
  entity: RerunEntity;
  evaluation: Evaluation;
  applications: Record<string, unknown>;
  agentEmail: string;
  apiUrl: string;
  bearerToken: string;
  documentTypes: unknown[];
  groupId: boolean | string;
  isChildRun?: boolean;
  isSwitchWorkflowEnabled?: boolean;
  doPassOriginalEvaluationToken?: boolean;
  groupEntityTokenWithWhichToRerunEvaluation?: string;
  onCancel: () => void;
  onRerunEvaluation?: (data: { evaluationUrl?: string }) => void;
};

const RerunEvaluationFormPropTypes = {
  groupId: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]).isRequired,
  onCancel: PropTypes.func.isRequired,
  onRerunEvaluation: PropTypes.func,
  isSwitchWorkflowEnabled: PropTypes.bool,
  doPassOriginalEvaluationToken: PropTypes.bool,
  groupEntityTokenWithWhichToRerunEvaluation: PropTypes.string,
  ...RunEvaluationFormPropTypes,
};

class RerunEvaluationForm extends RunEvaluationForm {
  hasMetaFormData: boolean;

  constructor(props: RerunEvaluationFormProps) {
    super(props);

    this.renderSettingsFields = this.renderSettingsFields.bind(this);

    const formData = getRerunFormData(props.evaluation.final_response);
    this.hasMetaFormData = evaluationHasMetaFormData(props.evaluation.final_response);

    this.state.formData = formData;
    if (formData.representatives && (formData.representatives as unknown[]).length > 1) {
      range((formData.representatives as unknown[]).length).forEach(index => {
        this.state.expanded[`representatives_${index}`] = true;
      });
    }

    this.state.externalGroupId = props.groupId || 0;
    this.state.customAppVersion = false;
    this.state.settings.bypassFormatter = false;

    this.getApplicationFields({
      application_token: props.evaluation.application_token,
      public_version: props.evaluation.public_version,
    });
  }

  handleClose = () => {
    const { evaluation, onCancel } = this.props as RerunEvaluationFormProps;
    this.setState({
      formData: getRerunFormData(evaluation.final_response),
      customAppVersion: false,
      applicationId: false,
      applicationPublicVersion: false,
      newEvaluationUrl: '',
      errorMessage: '',
    }, () => {
      onCancel();
      this.getApplicationFields({
        application_token: evaluation.application_token,
        public_version: evaluation.public_version,
      });
    });
  };

  handleUseCustomAppVersion = (useCustomAppVersion: boolean) => {
    const newState = { customAppVersion: useCustomAppVersion };
    if (!newState.customAppVersion) {
      // @ts-ignore - this file is a temporary copy of a js file, we will rework all of this
      newState.applicationId = false;
      // @ts-ignore - this file is a temporary copy of a js file, we will rework all of this
      newState.applicationPublicVersion = false;
    }
    this.setState(newState, () => {
      const { evaluation } = this.props as RerunEvaluationFormProps;
      if (!newState.customAppVersion) {
        this.getApplicationFields({
          application_token: evaluation.application_token,
          public_version: evaluation.public_version,
        });
      }
    });
  };

  handleRunEvaluation = (event: React.FormEvent) => {
    event.preventDefault();
    const { errors } = this.state;
    const {
      entity,
      evaluation,
      applications,
      isChildRun,
      doPassOriginalEvaluationToken,
      groupEntityTokenWithWhichToRerunEvaluation,
      onRerunEvaluation,
    } = this.props as RerunEvaluationFormProps;

    if (errors.required.length === 0 && !errors.or) {
      const applicationToken = this.state.customAppVersion
        // @ts-ignore - this file is a temporary copy of a js file, we will rework all of this
        ? applications[this.state.applicationId].application_token
        : evaluation.application_token;
      const applicationVersion = this.state.customAppVersion
        ? this.state.applicationPublicVersion
        : evaluation.public_version;

      this.setState({ submissionInProgress: true });

      const formData = {
        ...(this.state.includeExtraData ? this.state.extraData : {}),
        ...this.state.formData,
      };
      if (this.hasMetaFormData) {
        // @ts-ignore - this file is a temporary copy of a js file, we will rework all of this
        formData.meta = {
          ...(this.state.extraData.meta || {}),
          // @ts-ignore - this file is a temporary copy of a js file, we will rework all of this
          ...(formData.meta || {}),
        };
      }
      const settings = {
        ...this.state.settings,
        sandboxServices: this.state.settings.sandboxServices.map(s => s.value).join(','),
      };

      const reqBody: Record<string, unknown> = {
        application_token: applicationToken,
        application_public_version: applicationVersion,
        evaluation_token: evaluation.evaluation_token,
        form_data: formattedFormData(formData),
        settings,
        alloy_internal_request_source: 'dashboard_rerun',
        ...(this.state.alloyAuditAccountSlugs
          ? { alloy_audit_account_slugs: this.state.alloyAuditAccountSlugs }
          : {}),
      };

      if (this.state.externalGroupId !== 0) {
        reqBody.external_group_identifier = this.state.externalGroupId;
      }

      if (isChildRun || entity.type === 'group') {
        reqBody.parent_entity_token = entity.entity_token;
      }

      if (groupEntityTokenWithWhichToRerunEvaluation) {
        reqBody.parent_entity_token = groupEntityTokenWithWhichToRerunEvaluation;
        reqBody.do_run_both_entity_and_parent_entity = true;
      }

      if (doPassOriginalEvaluationToken) {
        reqBody.original_evaluation_token = evaluation.evaluation_token;
      }

      if (entity.external_entity_identifier) {
        reqBody.alloy_external_entity_id = entity.external_entity_identifier;
      }

      const url = entity.evaluations_data_url
        || routes.entities.evaluation.create({ entityToken: entity.entity_token });

      ajaxFetch(
        url,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(reqBody),
        },
      ).then(data => {
        if (['error', 'data_request'].indexOf(data.result) > -1) {
          if (data.response.prompts && Object.keys(data.response.prompts).length) {
            this.setState({
              multiStep: {
                prompts: data.response.prompts,
                patchEvaluationToken: data.response.evaluation_token,
              },
            });
          } else if (
            data.response.error && data.response.error && data.response.error.details
          ) {
            if (data.response.error.details.message) {
              this.setState({ errorMessage: data.response.error.details.message });
            } else if (data.response.error.message) {
              this.setState({ errorMessage: data.response.error.message });
            }
          } else if (data.response.entity_token && data.response.evaluation_token) {
            const newEvaluationUrl = routes.entities.evaluation.detail({
              entityToken: data.response.entity_token,
              evaluationToken: data.response.evaluation_token,
            });
            this.setState({ errorMessage: '', newEvaluationUrl });
          } else {
            this.setState({
              errorMessage: 'Something went wrong. Please try again or contact support',
            });
          }
        } else {
          onRerunEvaluation?.(data);
          this.setState({ errorMessage: '', newEvaluationUrl: data.evaluationUrl });
        }
        this.setState({ submissionInProgress: false });
      });
    } else {
      this.setState({ displayRequiredErrors: true });
      const { addSnackBar } = this.props;
      addSnackBar({
        title: 'Please fix the errors with your form data before rerunning.',
        type: 'danger',
      });
    }
  };

  renderSettingsFields({ doShowSandboxSettings }) {
    const settingsFields = super.renderSettingsFields({ doShowSandboxSettings });
    settingsFields.splice(1, 0, (
      <CheckboxWrapper key={4} className="flex start h-space-16 header-8">
        <Checkbox
          name="bypassFormatter"
          id="bypassFormatter"
          checked={this.state.settings.bypassFormatter}
          onChange={this.handleSettingsChange}
        />
        <label
          id="bypassFormatterLabel"
          htmlFor="bypassFormatter"
        >
          Bypass Alloy Formatter
        </label>
        <Icon id="bypassFormatterLabelIcon" icon={IconName.Question} />
        <Tooltip
          target="bypassFormatterLabelIcon"
          placement="right"
          delay={0}
          modifiers={tooltipZIndexModifiers}
        >
          Disable Alloy&apos;s built-in formatter
        </Tooltip>
      </CheckboxWrapper>
    ));

    const { isSwitchWorkflowEnabled = true } = this.props;
    if (isSwitchWorkflowEnabled) {
      settingsFields.push((
        <div key={5} className="header-8">
          <CheckboxWrapper className="flex start h-space-16">
            <Checkbox
              name="customAppVersion"
              id="customAppVersion"
              checked={this.state.customAppVersion}
              onChange={event => this.handleUseCustomAppVersion(event.target.checked)}
            />
            <label htmlFor="customAppVersion">
              Rerun using a different Workflow
            </label>
          </CheckboxWrapper>

          {this.state.customAppVersion && (
            <div className="switch-workflow-selects header-8">
              {this.renderApplicationSelects()}

              <p className="switch-workflow-caption">
                Switching may cause loss of data
                due to variations in application content and fields
              </p>
            </div>
          )}
        </div>
      ));
    }

    return settingsFields;
  }

  renderSuccessActions = () => {
    const { isChildRun = false } = this.props as RerunEvaluationFormProps;
    const { newEvaluationUrl } = this.state;

    return (
      <Box gap={3}>
        <a href={newEvaluationUrl}>
          <Button variant={ButtonVariant.primary}>
            View {isChildRun ? 'New Child Entity' : 'Evaluation'}
          </Button>
        </a>
        <Button
          variant={ButtonVariant.secondary}
          onClick={this.handleClose}
        >
          Back to Evaluation
        </Button>
      </Box>
    );
  };

  render = () => {
    const { applicationFields, newEvaluationUrl, multiStep } = this.state;
    const applicationFieldCategories = this.categorizeApplicationFields(applicationFields);
    const {
      entity,
      isChildRun = false,
    } = this.props as RerunEvaluationFormProps;
    const { entity_token: token } = entity;
    const pageTitle = isChildRun
      ? `Run New Child Entity of ${token}`
      : 'Rerun Entity';

    const doShowSandboxSettings = Boolean(
      this.permissions['READ:Evaluations:rerun_form_sandbox_services_field'],
    );

    return (
      <Box
        direction="column"
        alignItems="stretch"
        style={{ padding: '0 var(--cl-space-6)' }}
        className="run-evaluation-wrapper"
        key={newEvaluationUrl}
      >
        {multiStep && (
          <MultiStepModal
            multiStep={multiStep}
            submitMultiStep={this.handleMultiStepSubmit}
            closeModal={this.handleCloseMultiStepModal}
          />
        )}

        <div className="row header">
          <div className="col">
            <h1>{pageTitle}</h1>
          </div>
        </div>

        <ErrorBoundary
          withResetChildren
          loggingCallback={error => {
            log('error', 'Error in Rerun Evaluation Form', { url: window.location.pathname }, error);
          }}
        >
          {newEvaluationUrl ? (
            <>
              <div className="row header-24">
                <div className="col text-center">
                  <EvaluationCompleteSVG
                    color={this.state.errorMessage === 'partial' ? '#C9412B' : '#463361'}
                  />
                </div>
              </div>

              <div className="row header-24">
                <div className="col">
                  <p>
                    This evaluation has been successfully run
                    {this.state.errorMessage === 'partial'
                      ? ', but there were service failures. '
                      : '. '}
                  </p>
                </div>
              </div>

              <div className="row header-24">
                <div className="col">
                  {this.renderSuccessActions()}
                </div>
              </div>
            </>
          ) : (
            <div className="formatted-information">
              <form
                ref={f => { this.form = f; }}
                onSubmit={this.handleRunEvaluation}
                autoComplete="stopItChrome"
              >
                {this.state.errorMessage && (
                  <div className="alert alert-danger text-center" role="alert">
                    <strong><Icon icon={IconName.Exclamation} /></strong>
                    {this.state.errorMessage}
                  </div>
                )}

                {{}.hasOwnProperty.call(this.state.applicationFields, 'error') ? (
                  <p>
                    Sorry! Looks like there&apos;s an error with the selected application
                    version. Please contact support@alloy.co if this problem continues.
                  </p>
                ) : (
                  <div>
                    {applicationFieldCategories && !!applicationFieldCategories.length && (
                      <div>
                        {this.renderOrApplicationFields()}
                        {this.renderApplicationFields(applicationFieldCategories)}
                      </div>
                    )}

                    {this.renderSettings({ doShowSandboxSettings })}
                  </div>
                )}

                <div className="row header">
                  <div className="col flex space">
                    <Button
                      type="button"
                      variant={ButtonVariant.secondary}
                      onClick={this.handleClose}
                    >
                      Cancel
                    </Button>

                    <Button
                      id="action-submit"
                      variant={ButtonVariant.primary}
                      disabled={this.state.submissionInProgress
                        || ({}.hasOwnProperty.call(this.state.applicationFields, 'error')
                          || (
                            this.state.customAppVersion
                            && (
                              this.state.applicationId === false
                              || this.state.applicationPublicVersion === false
                            )))}
                      onClick={this.handleRunEvaluation}
                    >
                      {this.state.submissionInProgress
                        ? 'Running Evaluation...'
                        : 'Run Evaluation'}
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          )}
        </ErrorBoundary>
      </Box>
    );
  };
}

RerunEvaluationForm.propTypes = RerunEvaluationFormPropTypes;
RerunEvaluationForm.defaultProps = {
  ...RunEvaluationFormDefaultProps,
  isSwitchWorkflowEnabled: true,
  isRerun: true,
};

const WrappedRerunEvaluationForm = WithDashboardHooksHOC(RerunEvaluationForm);

export default WrappedRerunEvaluationForm;
